'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, Package, Loader2, ChevronRight, Plus } from 'lucide-react';
import {
  type FinanceItem,
  type WorkItemInventory,
  type WarehouseStockLevel,
  type SupplierRelationshipSummary,
  type PurchaseLineSourcingDetails,
  mapWorkItemToFinanceItem,
  inventorySearchCache,
  buildInventorySearchCacheKey,
  setInventorySearchCache,
  clearInventorySearchCache,
} from './ItemSearchModal';
import { authService } from '@/lib/authService';

interface InlineItemSearchProps {
  onItemSelect: (item: FinanceItem) => void;
  onClose: () => void;
  // When provided, a "Create new item" action shows in the dropdown footer —
  // opens the quick-create slide-over so missing codes can be registered.
  onCreateNewItem?: (term: string) => void;
  documentType: 'sales' | 'purchase';
  contactId?: number;
  currency?: string;
  scopeItemsToCustomerContext?: boolean;
  isDarkMode?: boolean;
  autoFocus?: boolean;
  itemSubtype?: 'product' | 'service' | 'all';
}

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 0;

export default function InlineItemSearch({
  onItemSelect,
  onClose,
  onCreateNewItem,
  documentType,
  contactId,
  currency = 'ZAR',
  scopeItemsToCustomerContext = false,
  isDarkMode = false,
  autoFocus = true,
  itemSubtype = 'all',
}: InlineItemSearchProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<FinanceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [resolvedPrices, setResolvedPrices] = useState<Record<string, { unit_price: number; currency: string }>>({});
  const [stockLevelsByItemId, setStockLevelsByItemId] = useState<Record<string, WarehouseStockLevel[]>>({});
  const [supplierRelationshipsByItem, setSupplierRelationshipsByItem] = useState<Record<string, SupplierRelationshipSummary[]>>({});
  const [showOutOfStock, setShowOutOfStock] = useState(false);

  // Refs to avoid re-creating doSearch when these async-fetched maps update
  const stockLevelsRef = useRef(stockLevelsByItemId);
  stockLevelsRef.current = stockLevelsByItemId;
  const supplierRelationshipsRef = useRef(supplierRelationshipsByItem);
  supplierRelationshipsRef.current = supplierRelationshipsByItem;

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number; flipUp: boolean } | null>(null);
  const requestIdRef = useRef(0);
  const hasFetchedRef = useRef(false);

  // The listbox renders in a portal (fixed, on document.body) because the
  // inline search lives inside a table cell within a scrollable modal —
  // overflow clipping and dnd-kit transform stacking contexts would trap an
  // absolutely-positioned dropdown (the memo band painted over it).
  const DROPDOWN_MAX_HEIGHT = 264;
  const DROPDOWN_MIN_WIDTH = 300;
  const updateDropdownPos = useCallback(() => {
    const rect = inputRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < DROPDOWN_MAX_HEIGHT && rect.top > spaceBelow;
    setDropdownPos({
      top: flipUp ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, DROPDOWN_MIN_WIDTH),
      flipUp,
    });
  }, []);

  const normalizedContactId = (() => {
    if (contactId == null) return null;
    const next = Number(contactId);
    return Number.isFinite(next) && next > 0 ? next : null;
  })();

  const doSearch = useCallback(async (term: string) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const tokens = authService.getTokens();
      const accessToken = tokens?.access_token;
      if (!accessToken) {
        throw new Error('Not authenticated');
      }

      // Check shared module-level cache first
      const cacheKey = buildInventorySearchCacheKey({
        term,
        itemType: 'inventory',
        contactId: normalizedContactId,
        scopedToContact: scopeItemsToCustomerContext,
        itemSubtype,
      });
      const cachedItems = inventorySearchCache.get(cacheKey);
      if (cachedItems) {
        const mapped = cachedItems.map((item) => mapWorkItemToFinanceItem(item, stockLevelsRef.current));
        if (documentType === 'purchase' || showOutOfStock) {
          setResults(mapped);
        } else {
          setResults(mapped.filter((item) => !item.track_inventory || Number(item.quantity_available) > 0));
        }
        setHighlightedIndex(0);
        setIsOpen(true);
        setLoading(false);

        // Fetch supplier relationships for purchase documents (cache path)
        if (documentType === 'purchase') {
          const cachedItemIds = mapped.map((item) => item.id).filter(Boolean);
          if (cachedItemIds.length > 0) {
            void (async () => {
              try {
                const supplierResponse = await fetch(`/api/v1/work-items/inventory/suppliers?work_item_ids=${encodeURIComponent(cachedItemIds.join(','))}`, {
                  headers: { 'Authorization': `Bearer ${accessToken}` },
                });
                if (supplierResponse.ok) {
                  const supplierResult = await supplierResponse.json();
                  if (supplierResult?.success && Array.isArray(supplierResult.data)) {
                    const grouped: Record<string, SupplierRelationshipSummary[]> = {};
                    for (const row of supplierResult.data as Array<Record<string, unknown>>) {
                      const key = String(row.work_item_id || '');
                      if (!key) continue;
                      if (!grouped[key]) grouped[key] = [];
                      grouped[key].push({
                        id: Number(row.id),
                        work_item_id: key,
                        supplier_contact_id: Number(row.supplier_contact_id),
                        supplier_name: typeof row.supplier_name === 'string' ? row.supplier_name : null,
                        supplier_sku: typeof row.supplier_sku === 'string' ? row.supplier_sku : null,
                        cost_price: row.cost_price == null ? null : Number(row.cost_price),
                        currency_code: typeof row.currency_code === 'string' ? row.currency_code : null,
                        lead_time_days: row.lead_time_days == null ? null : Number(row.lead_time_days),
                        min_order_qty: row.min_order_qty == null ? null : Number(row.min_order_qty),
                        is_preferred: Boolean(row.is_preferred),
                        priority_rank: row.priority_rank == null ? null : Number(row.priority_rank),
                        selection_reason: typeof row.selection_reason === 'string' ? row.selection_reason : null,
                        fallback_reason: typeof row.fallback_reason === 'string' ? row.fallback_reason : null,
                      });
                    }
                    setSupplierRelationshipsByItem(grouped);
                  }
                }
              } catch {
                // Supplier relationships are best-effort
              }
            })();
          }
        }

        return;
      }

      const createParams = (requestedContactId?: number | null) => {
        const params = new URLSearchParams({
          item_type: 'inventory',
          page: '1',
          limit: normalizedContactId != null && scopeItemsToCustomerContext ? '100' : '50',
        });

        if (itemSubtype !== 'all') {
          params.append('item_subtype', itemSubtype);
        }

        if (term) {
          params.append('search', term);
        }

        if (requestedContactId != null) {
          params.append('contact_id', String(requestedContactId));
        }

        return params;
      };

      const fetchInventoryItems = async (params: URLSearchParams): Promise<WorkItemInventory[]> => {
        const response = await fetch(`/api/v1/work-items?${params.toString()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Search failed: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error || 'Search failed');
        }

        return Array.isArray(result.data) ? (result.data as WorkItemInventory[]) : [];
      };

      const [genericItems, scopedItems] = await Promise.all([
        fetchInventoryItems(createParams()),
        normalizedContactId != null && scopeItemsToCustomerContext
          ? fetchInventoryItems(createParams(normalizedContactId))
          : Promise.resolve([] as WorkItemInventory[]),
      ]);

      const mergedItems = [...scopedItems, ...genericItems].filter((item) => {
        if (normalizedContactId == null || !scopeItemsToCustomerContext) {
          return true;
        }
        const itemContactId = item.contact_id != null ? Number(item.contact_id) : null;
        return itemContactId == null || itemContactId === normalizedContactId;
      });

      const dedupedItems = mergedItems.reduce<WorkItemInventory[]>((acc, item) => {
        if (acc.some((existing) => existing.id === item.id)) {
          return acc;
        }
        acc.push(item);
        return acc;
      }, []);

      // Store in shared cache for other components (e.g. ItemSearchModal)
      setInventorySearchCache(cacheKey, dedupedItems);

      if (requestId !== requestIdRef.current) return;

      // Fetch warehouse stock levels for the deduped items
      const stockRequestId = ++requestIdRef.current;
      void (async () => {
        try {
          const stockParams = new URLSearchParams({
            include_zero: 'true',
            skip_summary: 'true',
            limit: String(Math.max(dedupedItems.length, 50)),
          });
          stockParams.set('work_item_ids', dedupedItems.map((item) => item.id).filter(Boolean).join(','));
          const stockResponse = await fetch(`/api/v1/work-items/inventory/stock-levels?${stockParams.toString()}`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
          });
          if (stockResponse.ok) {
            const stockResult = await stockResponse.json();
            if (stockResult?.success && Array.isArray(stockResult.data)) {
              const stockMap: Record<string, WarehouseStockLevel[]> = {};
              for (const sl of stockResult.data as WarehouseStockLevel[]) {
                if (!stockMap[sl.work_item_id]) stockMap[sl.work_item_id] = [];
                stockMap[sl.work_item_id].push(sl);
              }
              if (stockRequestId === requestIdRef.current) {
                setStockLevelsByItemId(stockMap);
              }
            }
          }
        } catch {
          // Stock levels are best-effort
        }
      })();

      const mapped = dedupedItems.map((item) => mapWorkItemToFinanceItem(item, stockLevelsRef.current));

      const itemIds = mapped.map((item) => item.id).filter(Boolean);

      // Fetch supplier relationships for purchase documents
      if (documentType === 'purchase' && itemIds.length > 0) {
        try {
          const supplierResponse = await fetch(`/api/v1/work-items/inventory/suppliers?work_item_ids=${encodeURIComponent(itemIds.join(','))}`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          });
          if (supplierResponse.ok) {
            const supplierResult = await supplierResponse.json();
            if (supplierResult?.success && Array.isArray(supplierResult.data)) {
              const grouped: Record<string, SupplierRelationshipSummary[]> = {};
              for (const row of supplierResult.data as Array<Record<string, unknown>>) {
                const key = String(row.work_item_id || '');
                if (!key) continue;
                if (!grouped[key]) grouped[key] = [];
                grouped[key].push({
                  id: Number(row.id),
                  work_item_id: key,
                  supplier_contact_id: Number(row.supplier_contact_id),
                  supplier_name: typeof row.supplier_name === 'string' ? row.supplier_name : null,
                  supplier_sku: typeof row.supplier_sku === 'string' ? row.supplier_sku : null,
                  cost_price: row.cost_price == null ? null : Number(row.cost_price),
                  currency_code: typeof row.currency_code === 'string' ? row.currency_code : null,
                  lead_time_days: row.lead_time_days == null ? null : Number(row.lead_time_days),
                  min_order_qty: row.min_order_qty == null ? null : Number(row.min_order_qty),
                  is_preferred: Boolean(row.is_preferred),
                  priority_rank: row.priority_rank == null ? null : Number(row.priority_rank),
                  selection_reason: typeof row.selection_reason === 'string' ? row.selection_reason : null,
                  fallback_reason: typeof row.fallback_reason === 'string' ? row.fallback_reason : null,
                });
              }
              if (requestId === requestIdRef.current) {
                setSupplierRelationshipsByItem(grouped);
              }
            }
          }
        } catch {
          // Supplier relationships are best-effort
        }
      }

      // Resolve customer-specific prices for the visible items
      if (itemIds.length > 0) {
        try {
          const priceResponse = await fetch('/api/v1/pricing/resolve', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              contact_id: contactId ?? null,
              currency,
              list_type: documentType === 'purchase' ? 'buying' : 'selling',
              items: itemIds.map((workItemId) => ({ work_item_id: workItemId, quantity: 1 })),
            }),
          });

          if (priceResponse.ok) {
            const priceResult = await priceResponse.json();
            if (priceResult?.success && Array.isArray(priceResult.data)) {
              const priceMap: Record<string, { unit_price: number; currency: string }> = {};
              for (const row of priceResult.data) {
                if (row?.work_item_id) {
                  priceMap[row.work_item_id] = {
                    unit_price: Number(row.unit_price ?? 0),
                    currency: String(row.currency || currency),
                  };
                }
              }
              if (requestId === requestIdRef.current) {
                setResolvedPrices(priceMap);
              }
            }
          }
        } catch {
          // Price resolution is best-effort; fall back to item base prices
        }
      }

      if (documentType === 'purchase' || showOutOfStock) {
        // For purchase documents or when toggle is on, don't filter out out-of-stock items
        setResults(mapped);
      } else {
        // For sales documents, filter to in-stock items by default
        setResults(mapped.filter((item) => !item.track_inventory || Number(item.quantity_available) > 0));
      }
      setHighlightedIndex(0);
      setIsOpen(true);
    } catch (err) {
      console.error('Inline item search error:', err);
      if (requestId === requestIdRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to search items');
        setResults([]);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [contactId, currency, documentType, itemSubtype, normalizedContactId, scopeItemsToCustomerContext, showOutOfStock]);

  // Clear shared cache on mount so items created/edited elsewhere are fresh
  useEffect(() => {
    clearInventorySearchCache();
  }, []);

  // Initial load on mount
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    doSearch('');
  }, [doSearch]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      doSearch(searchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm, doSearch]);

  // Auto-focus input
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Track the input's on-screen rect while the portaled listbox is open —
  // scroll on any ancestor (capture phase) and viewport resize both move it.
  useEffect(() => {
    if (!isOpen) return;
    updateDropdownPos();
    window.addEventListener('resize', updateDropdownPos);
    document.addEventListener('scroll', updateDropdownPos, true);
    return () => {
      window.removeEventListener('resize', updateDropdownPos);
      document.removeEventListener('scroll', updateDropdownPos, true);
    };
  }, [isOpen, updateDropdownPos]);

  // Click outside to close — the portaled listbox lives outside containerRef,
  // so both refs must be consulted.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target)
        && !(dropdownRef.current && dropdownRef.current.contains(target))
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const getPurchaseLineSourcing = useCallback((item: FinanceItem): PurchaseLineSourcingDetails | undefined => {
    if (documentType !== 'purchase') return undefined;
    const relationships = supplierRelationshipsByItem[item.id] || [];
    const selectedSupplier = normalizedContactId != null
      ? relationships.find((r) => r.supplier_contact_id === normalizedContactId) || null
      : null;
    const preferredSupplier = relationships.find((r) => r.is_preferred) || relationships[0] || null;
    const effectiveSupplier = selectedSupplier || preferredSupplier;
    if (!effectiveSupplier) return undefined;
    const priceSource: PurchaseLineSourcingDetails['price_source'] = selectedSupplier
      ? (selectedSupplier.is_preferred ? 'selected_preferred_supplier' : 'selected_supplier_mapping')
      : effectiveSupplier.is_preferred
        ? 'preferred_supplier_mapping'
        : 'supplier_mapping';
    return {
      supplier_mapping_id: effectiveSupplier.id,
      supplier_contact_id: effectiveSupplier.supplier_contact_id,
      supplier_name: effectiveSupplier.supplier_name ?? null,
      supplier_sku: effectiveSupplier.supplier_sku ?? null,
      currency_code: effectiveSupplier.currency_code ?? null,
      cost_price: effectiveSupplier.cost_price ?? null,
      lead_time_days: effectiveSupplier.lead_time_days ?? null,
      min_order_qty: effectiveSupplier.min_order_qty ?? null,
      is_preferred: effectiveSupplier.is_preferred,
      priority_rank: effectiveSupplier.priority_rank ?? null,
      selection_reason: effectiveSupplier.selection_reason ?? null,
      fallback_reason: effectiveSupplier.fallback_reason ?? null,
      price_source: priceSource,
      matches_document_contact: selectedSupplier != null,
    };
  }, [documentType, supplierRelationshipsByItem, normalizedContactId]);

  const handleSelect = (item: FinanceItem) => {
    const purchaseSourcing = getPurchaseLineSourcing(item);
    const resolved = resolvedPrices[item.id];
    const effectivePrice = documentType === 'purchase' && purchaseSourcing?.cost_price != null
      ? purchaseSourcing.cost_price
      : resolved?.unit_price ?? item.unit_price;
    const effectiveCurrency = documentType === 'purchase' && purchaseSourcing?.currency_code
      ? purchaseSourcing.currency_code
      : resolved?.currency || item.currency;
    onItemSelect({
      ...item,
      unit_price: effectivePrice,
      currency: effectiveCurrency,
      ...(purchaseSourcing ? { purchase_sourcing: purchaseSourcing } : {}),
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[highlightedIndex]) {
        handleSelect(results[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const darkClasses = isDarkMode
    ? 'bg-gray-800 border-gray-600 text-gray-200 placeholder-gray-500'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400';
  const darkDropdownClasses = isDarkMode
    ? 'bg-gray-800 border-gray-600'
    : 'bg-white border-gray-200';
  const darkItemClasses = isDarkMode
    ? 'hover:bg-gray-700 text-gray-200'
    : 'hover:bg-blue-50 text-gray-700';
  const darkItemActiveClasses = isDarkMode
    ? 'bg-blue-900/40 text-blue-200'
    : 'bg-blue-50 text-blue-700';

  return (
    <div ref={containerRef} data-tour-id="inline-item-search" className="relative w-full">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search items..."
          role="combobox"
          aria-expanded={isOpen}
          aria-controls="inline-item-search-listbox"
          aria-activedescendant={isOpen && results[highlightedIndex] ? `inline-item-option-${results[highlightedIndex].id}` : undefined}
          aria-autocomplete="list"
          className={`w-full pl-7 pr-7 py-1.5 text-xs border rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none ${darkClasses}`}
        />
        {loading && (
          <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 animate-spin" />
        )}
      </div>

      {documentType === 'sales' && (
        <div className="flex items-center gap-1 mt-0.5">
          <label className={`flex items-center gap-1 text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <input
              type="checkbox"
              checked={showOutOfStock}
              onChange={(e) => setShowOutOfStock(e.target.checked)}
              className="w-3 h-3"
            />
            Show out-of-stock
          </label>
        </div>
      )}

      {isOpen && dropdownPos && (results.length > 0 || error || (!loading && searchTerm.length >= MIN_SEARCH_LENGTH)) && createPortal(
        <div
          ref={dropdownRef}
          id="inline-item-search-listbox"
          role="listbox"
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            maxHeight: DROPDOWN_MAX_HEIGHT,
            ...(dropdownPos.flipUp ? { transform: 'translateY(-100%)' } : {}),
            zIndex: 150,
          }}
          className={`overflow-y-auto rounded-lg border shadow-lg ${darkDropdownClasses}`}
        >
          {error && (
            <div className="px-3 py-2 text-xs text-red-500">{error}</div>
          )}
          {!error && !loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-400">No items found</div>
          )}
          {results.map((item, idx) => {
            const resolved = resolvedPrices[item.id];
            const displayPrice = resolved ? resolved.unit_price : item.unit_price;
            const displayCurrency = resolved ? resolved.currency : currency;
            return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleSelect(item)}
              role="option"
              id={`inline-item-option-${item.id}`}
              aria-selected={idx === highlightedIndex}
              className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors ${idx === highlightedIndex ? darkItemActiveClasses : darkItemClasses}`}
            >
              <Package className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{item.name}</div>
                <div className="flex items-center gap-2 text-[10px] text-gray-400">
                  <span className="font-mono">{item.item_code}</span>
                  {item.track_inventory && (
                    <span className={Number(item.quantity_available) > 0 ? 'text-emerald-500' : 'text-red-400'}>
                      Stock: {item.quantity_available}
                    </span>
                  )}
                  {Number(displayPrice) > 0 && (
                    <span>{displayCurrency} {Number(displayPrice).toFixed(2)}</span>
                  )}
                </div>
              </div>
              <ChevronRight className="w-3 h-3 flex-shrink-0 text-gray-300" />
            </button>
            );
          })}
          {onCreateNewItem && (
            <button
              type="button"
              onClick={() => onCreateNewItem(searchTerm.trim())}
              className={`w-full text-left px-3 py-2 text-xs border-t flex items-center gap-2 transition-colors ${isDarkMode
                ? 'border-gray-600 text-blue-300 hover:bg-gray-700'
                : 'border-gray-200 text-blue-600 hover:bg-blue-50'}`}
            >
              <Plus className="w-3.5 h-3.5 flex-shrink-0" />
              <span>
                {searchTerm.trim()
                  ? <>Create item <span className="font-mono font-medium">&quot;{searchTerm.trim()}&quot;</span></>
                  : 'Create new item'}
              </span>
            </button>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
